import { useTheme } from '@mui/material'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useTranslation } from 'react-i18next'
import StyledTextField from '../../components/Layout/StyledTextField'
import { headingFontFamilies } from '../../theme/options/typography'

export interface OneTimePasscodeInputProps {
  /**
   * The number of digits expected in the one-time passcode.
   * @default 6
   */
  numDigits?: number

  /**
   * True if the code entered by the user is invalid.
   * @default false
   */
  invalidCode: boolean

  /**
   * State function to set the code entered by the user.
   * @param code {string} - The one-time passcode entered by the user.
   */
  setCode: (code: string) => void
}

/**
 * A component for inputting a one-time passcode.
 * @param props {OneTimePasscodeInputProps}
 */
const OneTimePasscodeInput: React.FC<OneTimePasscodeInputProps> = ({
  numDigits = 6,
  invalidCode,
  setCode,
}: OneTimePasscodeInputProps) => {
  const theme = useTheme()
  const { t } = useTranslation('vpc', { keyPrefix: 'confirmEmail' })
  return (
    <>
      <StyledTextField
        data-testid="otp-input"
        aria-label={t('codeInput.ariaLabel')}
        placeholder={'0'.repeat(numDigits)}
        error={invalidCode}
        onChange={(e) => setCode(e.target.value)}
        sx={{ maxWidth: `${(numDigits + 1) * 4}rem`, width: 'unset' }}
        inputProps={{
          sx: {
            fontFamily: headingFontFamilies,
            textAlign: 'center',
            fontSize: '2rem',
            letterSpacing: '1rem',
            ml: '1rem',
            [theme.breakpoints.up('sm')]: {
              fontSize: '4rem',
              letterSpacing: '2rem',
              ml: '2rem',
            },
          },
          'aria-label': t('totpFieldLabel', { ns: 'common', keyPrefix: 'auth' }),
          inputMode: 'numeric',
          pattern: '[0-9]*',
          maxLength: numDigits,
          minLength: numDigits,
          'data-1p-ignore': true,
          'data-lpignore': true,
          'data-form-type': 'other',
          'data-bwignore': true,
        }}
      />
      {invalidCode && (
        <Typography
          component="p"
          sx={{
            color: theme.palette.error.dark,
          }}
        >
          {t('invalidCode')}
        </Typography>
      )}
    </>
  )
}

export default OneTimePasscodeInput
